import React from 'react';
import * as htmlToImage from 'html-to-image';
import { ReactFlowInstance } from 'reactflow';

interface CaptureButtonProps {
    flowRef: React.RefObject<HTMLDivElement>;
    reactFlow: ReactFlowInstance;
    isCapturing: boolean;
    setIsCapturing: (value: boolean) => void;
}

const calculateViewport = (reactFlow: ReactFlowInstance, flowElement: HTMLElement) => {
    const nodesBounds = reactFlow.getNodes().reduce(
        (bounds, node) => {
            bounds.minX = Math.min(bounds.minX, node.position.x);
            bounds.minY = Math.min(bounds.minY, node.position.y);
            bounds.maxX = Math.max(bounds.maxX, node.position.x + (node.width || 200));
            bounds.maxY = Math.max(bounds.maxY, node.position.y + (node.height || 100));
            return bounds;
        },
        { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
    );

    const padding = 50;
    const width = nodesBounds.maxX - nodesBounds.minX + (padding * 2);
    const height = nodesBounds.maxY - nodesBounds.minY + (padding * 2);

    const zoom = Math.min(
        flowElement.offsetWidth / width,
        flowElement.offsetHeight / height
    );

    const centerX = (nodesBounds.maxX + nodesBounds.minX) / 2;
    const centerY = (nodesBounds.maxY + nodesBounds.minY) / 2;

    return {
        x: flowElement.offsetWidth / 2 - centerX * zoom,
        y: flowElement.offsetHeight / 2 - centerY * zoom,
        zoom: zoom * 0.95,
        width: width * zoom,
        height: height * zoom
    };
};

const CaptureButton: React.FC<CaptureButtonProps> = ({ flowRef, reactFlow, isCapturing, setIsCapturing }) => {
    const downloadImage = React.useCallback(() => {
        if (flowRef.current === null) {
            return;
        }

        // Emite evento de início da captura
        const startEvent = new CustomEvent('captureStart', {
            detail: { message: 'Iniciando captura da imagem' },
            bubbles: true,
            composed: true
        });
        flowRef.current.dispatchEvent(startEvent);

        setIsCapturing(true);

        const flowElement = flowRef.current.querySelector('.react-flow') as HTMLElement;
        if (!flowElement) return;

        const viewportInitial = reactFlow.getViewport();
        const newViewport = calculateViewport(reactFlow, flowElement);

        reactFlow.setViewport({
            x: newViewport.x,
            y: newViewport.y,
            zoom: newViewport.zoom
        }, { duration: 0 });

        setTimeout(() => {
            htmlToImage
                .toPng(flowElement, {
                    quality: 1,
                    backgroundColor: '#ffffff',
                    width: Math.max(newViewport.width, flowElement.offsetWidth),
                    height: Math.max(newViewport.height, flowElement.offsetHeight),
                    style: {
                        transform: 'scale(1)',
                        transformOrigin: 'top left'
                    },
                    filter: (node) => {
                        const exclude = ['react-flow__controls', 'react-flow__minimap', 'capture-button'];
                        return !exclude.some(className =>
                            node.classList?.contains(className)
                        );
                    }
                })
                .then((dataUrl) => {
                    const link = document.createElement('a');
                    link.download = 'diagrama.png';
                    link.href = dataUrl;
                    link.click();

                    reactFlow.setViewport(viewportInitial);
                    setIsCapturing(false);

                    // Emite evento de finalização da captura
                    const finishEvent = new CustomEvent('captureFinish', {
                        detail: { message: 'Captura finalizada com sucesso', success: true },
                        bubbles: true,
                        composed: true
                    });
                    flowRef.current?.dispatchEvent(finishEvent);
                })
                .catch((error) => {
                    console.error('Erro ao gerar imagem:', error);
                    setIsCapturing(false);

                    // Emite evento de erro na captura
                    const errorEvent = new CustomEvent('captureFinish', {
                        detail: { message: 'Erro ao gerar imagem', success: false, error },
                        bubbles: true,
                        composed: true
                    });
                    flowRef.current?.dispatchEvent(errorEvent);
                });
        }, 100);
    }, [reactFlow, flowRef, setIsCapturing]);

    return (
        <button
            id="capture-button"
            onClick={downloadImage}
            className="capture-button"
            style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                border: '1px solid #D1D5DB',
                backgroundColor: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
            }}
            title="Salvar como imagem">
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#111827">
                <path d="M480-260q75 0 127.5-52.5T660-440q0-75-52.5-127.5T480-620q-75 0-127.5 52.5T300-440q0 75 52.5 127.5T480-260Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29ZM160-120q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h126l74-80h240l74 80h126q33 0 56.5 23.5T880-680v480q0 33-23.5 56.5T800-120H160Zm0-80h640v-480H638l-73-80H395l-73 80H160v480Zm320-240Z" />
            </svg>
        </button>
    );
};

export default CaptureButton;

